import { WellbeingFactors, apple_health_data, Plan } from '../types/patient';

/**
 * Default wellbeing factors for uploaded patients.
 * This data is used when EHR data doesn't include wellbeing assessments.
 */
export const defaultWellbeingFactors: WellbeingFactors = {
  mentalHealth: {
    depression: { score: 7, comment: "No significant concerns reported." },
    anxiety: { score: 7, comment: "Minimal anxiety reported." },
    stress: { score: 6, comment: "Moderate stress levels." }
  },
  socialFactors: {
    support: { score: 6, comment: "Adequate social support network." },
    isolation: { score: 7, comment: "Not socially isolated." }
  },
  lifestyle: {
    exercise: { score: 6, comment: "Moderate physical activity." },
    nutrition: { score: 6, comment: "Average dietary habits." },
    sleep: { score: 6, comment: "Generally adequate sleep." },
    smoking: { score: 10, comment: "Non-smoker." },
    alcohol: { score: 8, comment: "Minimal alcohol consumption." }
  },
  environmental: {
    foodInsecurity: { score: 9, comment: "No food security concerns." },
    financialStress: { score: 7, comment: "Minimal financial stress reported." },
    housing: { score: 9, comment: "Stable housing situation." }
  }
};

/**
 * Generate sample Apple Health data for the past 30 days.
 * This provides placeholder wearable data for uploaded patients.
 */
export function generateDefaultAppleHealthData(): apple_health_data[] {
  const data: apple_health_data[] = [];
  const today = new Date();

  for (let i = 29; i >= 0; i--) {
    const date = new Date(today);
    date.setDate(date.getDate() - i);

    data.push({
      date: date.toISOString().split('T')[0],
      activeEnergyBurned: Math.floor(Math.random() * 300) + 200,
      basalEnergyBurned: Math.floor(Math.random() * 200) + 1400,
      distanceWalkingRunning: Math.round((Math.random() * 3 + 1) * 100) / 100,
      flightsClimbed: Math.floor(Math.random() * 10) + 2,
      stepCount: Math.floor(Math.random() * 5000) + 4000,
      walkingSpeed: Math.round((Math.random() * 1.5 + 2.5) * 100) / 100
    });
  }

  return data;
}

/**
 * Default health plan template for uploaded patients.
 */
export const defaultHealthPlan: Plan = {
  "vitals": {
    categoryName: "Vitals & Key Biometrics",
    goals: [
      {
        id: "bp",
        metricName: "Blood Pressure",
        targetDisplay: "< 120/80 mmHg",
        currentValue: "Not measured",
        unit: "mmHg",
        actionableSteps: ["Monitor blood pressure regularly", "Reduce sodium intake"],
        source: "Default template",
        priority: "High",
        visualizationType: "Gauge"
      },
      {
        id: "weight",
        metricName: "Weight",
        targetDisplay: "Maintain healthy BMI",
        currentValue: "Not recorded",
        unit: "lbs",
        actionableSteps: ["Log meals daily", "Exercise 30 minutes"],
        source: "Default template",
        priority: "Medium",
        visualizationType: "LineGraph"
      }
    ]
  },

  "activity": {
    categoryName: "Physical Activity",
    goals: [
      {
        id: "steps",
        metricName: "Daily Steps",
        targetDisplay: 8000,
        currentValue: 0,
        unit: "steps",
        actionableSteps: ["Take a 20-minute walk after lunch"],
        source: "Default template",
        priority: "Medium",
        visualizationType: "ProgressBar"
      },
      {
        id: "active-minutes",
        metricName: "Daily Active Minutes",
        targetDisplay: 30,
        currentValue: 0,
        unit: "minutes",
        actionableSteps: ["Go for a walk", "Try a home workout"],
        source: "Default template",
        priority: "High",
        visualizationType: "ProgressBar"
      }
    ]
  },

  "medication": {
    categoryName: "Medication & Adherence",
    goals: [
      {
        id: "med-adherence",
        metricName: "Medication Adherence",
        targetDisplay: "100%",
        currentValue: "Not tracked",
        unit: "%",
        actionableSteps: ["Set daily phone reminder", "Use weekly pill organizer"],
        source: "Default template",
        priority: "High",
        visualizationType: "Checklist"
      }
    ]
  },

  "sleep": {
    categoryName: "Sleep",
    goals: [
      {
        id: "sleep-duration",
        metricName: "Sleep Duration",
        targetDisplay: "7-9 hours",
        currentValue: "Not recorded",
        unit: "hours",
        actionableSteps: ["No screens 1 hour before bed", "Set consistent bedtime"],
        source: "Default template",
        priority: "Medium",
        visualizationType: "LineGraph"
      }
    ]
  }
};

/**
 * Default health metrics for when vitals are not available from EHR
 */
export const defaultHealthMetrics = {
  metabolicSyndrome: {
    risk: 'low' as const,
    score: 0,
    factors: [] as string[]
  },
  hypertension: {
    systolic: 120,
    diastolic: 80,
    risk: 'low' as const
  },
  liverEnzymes: {
    alt: 25,
    ast: 25,
    risk: 'low' as const
  },
  obesity: {
    bmi: 24,
    risk: 'low' as const
  },
  heartDisease: {
    cholesterol: {
      total: 180,
      ldl: 100,
      hdl: 50
    },
    risk: 'low' as const
  }
};
